/**
 * 全球时区配置
 * 使用浏览器内置的 Intl API 动态获取所有支持的时区
 */

// 常用时区列表（排序优先级）
const COMMON_TIMEZONES = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Singapore',
  'Asia/Taipei',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'UTC'
];

/**
 * 获取所有支持的时区
 * 使用浏览器内置的 Intl API
 */
function getAllTimezones() {
  try {
    // 使用 Intl API 获取所有支持的时区
    if (typeof Intl !== 'undefined' && Intl.supportedValuesOf) {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (error) {
    console.warn('Intl.supportedValuesOf not supported, using fallback list');
  }
  
  // 降级：如果浏览器不支持，返回常用时区列表
  return [
    // 亚洲
    'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Hong_Kong',
    'Asia/Singapore', 'Asia/Bangkok', 'Asia/Dubai', 'Asia/Kolkata',
    // 美洲
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'America/Mexico_City',
    // 欧洲
    'Europe/London', 'Europe/Paris', 'Europe/Moscow',
    // 大洋洲
    'Australia/Sydney', 'Pacific/Auckland',
    // UTC
    'UTC'
  ];
}

/**
 * 获取时区的UTC偏移量（小时）
 * 使用 Intl.DateTimeFormat 的 longOffset 直接产出 "GMT+08:00" 标准偏移串，
 * 与浏览器本地时区无关；支持 30/45 分钟等非整小时偏移
 */
function getTimezoneOffset(timezone) {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    }).formatToParts(now);
    const timezoneName = parts.find(part => part.type === 'timeZoneName').value;

    if (!timezoneName || timezoneName === 'GMT' || timezoneName === 'UTC') return 0;

    const match = timezoneName.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/);
    if (!match) return 0;

    const offset = Number(match[2]) + Number(match[3] || 0) / 60;
    return match[1] === '-' ? -offset : offset;
  } catch (error) {
    return 0;
  }
}

/**
 * 格式化UTC偏移量为字符串
 */
function formatOffset(offset) {
  const sign = offset >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offset));
  const minutes = Math.round((Math.abs(offset) % 1) * 60);
  
  if (minutes === 0) {
    return `UTC${sign}${hours.toString().padStart(2, '0')}:00`;
  } else {
    return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
}

/**
 * 生成时区列表（带显示标签和偏移量）
 * 常用时区排在最前面
 */
export function getTimezones() {
  const timezones = getAllTimezones();
  
  const timezoneList = timezones.map(tz => {
    const offset = getTimezoneOffset(tz);
    // 直接使用标准时区名称，将下划线替换为空格
    const label = tz.replace(/_/g, ' ');
    // 清理标签中的多余空格
    const cleanLabel = label.replace(/\s+/g, ' ').trim();
    
    return {
      value: tz,
      label: `(${formatOffset(offset)}) ${cleanLabel}`,
      offset: offset,
      isCommon: COMMON_TIMEZONES.includes(tz)
    };
  });
  
  // 分组：常用时区 + 其他时区
  const commonList = timezoneList.filter(tz => tz.isCommon);
  const otherList = timezoneList.filter(tz => !tz.isCommon);
  
  // 常用时区按预定义顺序排序
  commonList.sort((a, b) => {
    return COMMON_TIMEZONES.indexOf(a.value) - COMMON_TIMEZONES.indexOf(b.value);
  });
  
  // 其他时区按偏移量排序
  otherList.sort((a, b) => {
    if (a.offset !== b.offset) {
      return a.offset - b.offset;
    }
    return a.value.localeCompare(b.value);
  });
  
  // 合并：常用时区在前
  return [...commonList, ...otherList];
}

/**
 * 根据时区值查找时区信息
 */
export function findTimezone(value) {
  const timezones = getTimezones();
  return timezones.find(tz => tz.value === value) || null;
}

/**
 * 搜索时区
 */
export function searchTimezones(query) {
  if (!query) {
    return getTimezones();
  }
  
  const lowerQuery = query.toLowerCase();
  const timezones = getTimezones();
  
  return timezones.filter(tz => {
    return tz.label.toLowerCase().includes(lowerQuery) ||
           tz.value.toLowerCase().includes(lowerQuery);
  });
}
